import React, { FC, useState, useEffect } from 'react';
import {
    PrimaryButton,
    Icon,
    IDropdownOption,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
} from '@fluentui/react';
import {
    Dialog,
    TextField,
    required,
    Dropdown,
    Checkbox,
    CopyTextField
} from '@eta/components';
import { useTranslation } from 'react-i18next';
import { ConnectedProps, connect } from 'react-redux';
import { isNull } from 'util';
import {
    LanguageTypes,
    ProfileTypes,
    TaxpayerAccountingSystemDto,
    RegisterDelegateDto,
    RepresentationInvitationDto,
} from '../../../Services/ClientProxy/clientProxy';
import { ReduxState } from '../../../common/reducers';
import { addErpSystem } from '../../Redux/Actions';

import './AddERP.scss';

export interface AddERPProps {
    hidden: boolean;
    onClose: () => void;
    onSave: () => void;
    language: LanguageTypes;
    rin?: string;
}

const mapStateToProps = (state: ReduxState) => ({
    lastError: state.taxpayerProfile.lastError,
});

const mapDispatchToProps = {
    dispatchAddErpSystem: addErpSystem,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const AddERP: FC<PropsFromRedux & AddERPProps> = (
    props: PropsFromRedux & AddERPProps
) => {
    const { t } = useTranslation(['resources']);
    const { dispatchAddErpSystem, hidden, lastError, onClose, onSave } = props;
    const [Name, setName] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [CallbackURL, setcallbackURL] = useState<string>('');
    const [APIKey, setAPIKey] = useState<string>('');
    const [PrimaryCheackbox, setPrimaryCheackbox] = useState<
        boolean
    >(false);

    const [clientID, setclientID] = useState<string>('');
    const [clientSecret1, setclientSecret1] = useState<string>('');
    const [clientSecret2, setclientSecret2] = useState<string>('');

    const [, setclientIDCopied] = useState<boolean>(false);
    const [, setclientSecret1Copied] = useState<boolean>(false);
    const [, setclientSecret2Copied] = useState<boolean>(false);

    const [ClientSecretexpiration, setclientSecretexpiration] = useState<
        number
    >(1);
    const [Dis, setDis] = useState<boolean>(false);

    const [Page, setPage] = useState<boolean>(true);

    const [error, setError] = useState<string>();

    const ClientSecretexpirations: IDropdownOption[] = [
        { index: 1, key: 1, text: t('1 Year') },
        { index: 2, key: 2, text: t('2 Year') },
        { index: 3, key: 3, text: t('3 Year') },
    ];

    const SaveOnClick = async () => {
        setDis(true);
        setLoading(true);
        const request = new RegisterDelegateDto({
            preferredLanguage: props.language,
            userProfileType: ProfileTypes.System,
            taxpayerAccountingSystem: new TaxpayerAccountingSystemDto({
                namePrimaryLang: Name,
                nameSecondaryLang: Name,
                callbackUrl: CallbackURL === ''
                    ? ''
                    : `https://${CallbackURL}`,

                clientSecretExpiryInYears: ClientSecretexpiration,
                apiKey: APIKey,
                isMainTaxpayerAccountingSystem: PrimaryCheackbox,
            }),
            representationInvitation: new RepresentationInvitationDto(),
        });
        const response = await dispatchAddErpSystem(request);
        if (isNull((await response.payload).lastError)) {

            setclientID((await response.payload)?.response?.clientSecrets?.clientID ?? '');
            setclientSecret1((await response.payload)?.response?.clientSecrets?.clientSecretOne ?? '');
            setclientSecret2((await response.payload)?.response?.clientSecrets?.clientSecretTow ?? '');
            setPage(false);
        }
        setDis(false);
        setLoading(false);
    };

    const Done = () => {
        setLoading(true);
        onSave();
        setLoading(false);
        setPage(true);
    };

    useEffect(() => {
        setError(lastError?.serviceError?.toString());
    }, [lastError]);

    useEffect(() => {
        if (!hidden) {
            setName('');
            setcallbackURL('');
            setAPIKey('');
            setPrimaryCheackbox(false);
            setError('');
        }
    }, [hidden]);

    const closeDialog = () => {
        if (Page) {
            onClose();
        }
    };

    return (
        <Dialog
            hidden={hidden}
            onDismiss={() => {
                closeDialog();
            }}
            maxWidth={450}
            title={(
                <>
                    <Icon iconName="System" />{' '}
                    {t('form.Taxpayer.AddERPShowModal')}
                </>
            )}
        >
            <div className="addErp">
                {Page ? (

                    <div>
                        {error && (
                            <div className="errorMessage">
                                <MessageBar
                                    messageBarType={MessageBarType.error}
                                    isMultiline={false}
                                    onDismiss={() => setError('')}
                                    truncated
                                >
                                    {error}
                                </MessageBar>
                            </div>
                        )}
                        <div className="contentNoteErp">
                            <p>{t('form.erp.Content')}</p>
                        </div>
                        <div className="bodyLine">
                            <TextField
                                maxLength={50}
                                value={Name}
                                label={t('form.erp.Name')}
                                onChange={(_, v) => setName(v ?? '')}
                                required
                                placeholder={t('ToolTip.ErpName')}
                            />
                        </div>
                        <div className="ClientSecretExpiration bodyLine">
                            <Dropdown
                                selectedKey={ClientSecretexpiration}
                                toolTip={t('ToolTip.ClientSecretExpiration')}
                                options={ClientSecretexpirations}
                                label={t('form.erp.ClientSecretexpiration')}
                                required
                                validationFunc={(v) =>
                                    required(
                                        t,
                                        v,
                                        t('form.erp:clientSecretexpiration')
                                    )
                                }
                                onChange={(_, option) =>
                                    setclientSecretexpiration(
                                        option?.key ? Number(option.key) : 0
                                    )
                                }
                            />
                        </div>

                        <div className="bodyLine">
                            <TextField
                                className="prefixLTR"
                                label={t('form.erp.CallbackURL')}
                                placeholder={t('ToolTip.CallbackURL')}
                                onChange={(_, v) => setcallbackURL(v ?? '')}
                                value={CallbackURL}
                                prefix="https://"
                            />
                        </div>
                        <div className="apiKey bodyLine">
                            <TextField
                                label={t('form.erp.APIKey')}
                                placeholder={t('form.Erp.APIKey')}
                                onChange={(_, v) => setAPIKey(v ?? '')}
                                value={APIKey}

                            />
                        </div>

                        <div className="bodyLine flex">
                            <Checkbox
                                onChange={(_, checked) =>
                                    setPrimaryCheackbox(checked??false)
                                }
                            />
                            <span>{t('form.erp.PrimaryERPSystem')}</span>
                        </div>

                        <div className="bodyLine doneBtn">
                            <br />
                            <PrimaryButton
                                className="Register"
                                onClick={() => SaveOnClick()}
                                disabled={Dis || loading}
                            >
                                {t('form.erp.Register')}
                            </PrimaryButton>
                        </div>
                        <div className="navigation">
                            <Icon
                                iconName="LocationDot"
                                className="Icon d-active"
                            />
                            <Icon
                                iconName="LocationDot"
                                className="Icon active"
                            />

                        </div>
                        {loading && (
                    <div className="spinnerOnButtonCLick">
                        <Spinner
                            size={SpinnerSize.large}
                            label={t('form.TaxPayer.loading')}
                            ariaLive="assertive"
                            labelPosition="bottom"
                        />
                    </div>
                )}
                    </div>
                ) : (
                        <div>
                            <div className="ClientSecret">
                                <p>{t('form.erp.Secret1')}</p>
                                <p>{t('form.erp.Secret2')}</p>
                            </div>
                            <div className="bodyLine ClientDetails">
                                <CopyTextField
                                    label={t('form.erp.ClientID')}
                                    value={clientID}
                                    copiedTitle={t('copied')}
                                    onCopy={() => setclientIDCopied(true)}
                                />
                            </div>
                            <div className="bodyLine ClientDetails">
                                <CopyTextField
                                    label={t('form.erp.ClientSecret1')}
                                    value={clientSecret1}
                                    copiedTitle={t('copied')}
                                    onCopy={() => setclientSecret1Copied(true)}
                                />
                            </div>
                            <div className="bodyLine ClientDetails">
                                <CopyTextField
                                    label={t('form.erp.ClientSecret2')}
                                    value={clientSecret2}
                                    copiedTitle={t('copied')}
                                    onCopy={() => setclientSecret2Copied(true)}
                                />
                            </div>
                            <div className="bodyLine flex">
                                <Checkbox
                                    onChange={(_, checked) =>
                                        setPrimaryCheackbox(checked??false)
                                    }
                                />
                                {t('form.erp.confirmCheckbox')}
                            </div>

                            <div className="bodyLine doneBtn">                                
                                    <PrimaryButton
                                        text={t('form.erp.Done')}
                                        onClick={() => Done()}
                                        disabled={!PrimaryCheackbox || loading}
                                    />
                            </div>
                            <div className="navigation">

                                <Icon
                                    iconName="LocationDot"
                                    className="Icon active"
                                />
                                <Icon
                                    iconName="LocationDot"
                                    className="Icon d-active"
                                />
                            </div>
                            {loading && (
                    <div className="spinnerOnButtonCLick">
                        <Spinner
                            size={SpinnerSize.large}
                            label={t('form.TaxPayer.loading')}
                            ariaLive="assertive"
                            labelPosition="bottom"
                        />
                    </div>
                )}
                        </div>
                    )}
            </div>
        </Dialog>
    );
};

export default connector(AddERP);
